// Line-item dialog of the order editor: legacy EditRowItemComponent (sales /
// purchases) merged with SelectVariationComponent (the "choose portion /
// modifiers / variation / serials" modal shown when a complex item is added).

import { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { Add, Trash } from 'iconsax-reactjs';

import { t } from '../../i18n';
import {
  countSerialRanges,
  metaLabel,
  newSelectedPortion,
  num,
  type OrderKind,
  type OrderLine,
  type SelectedModifier,
  type SelectedPortion,
  type SelectedVariation,
  type Tax,
  valueForUnit
} from './orderModel';
import { UI } from '../../brand/ui';

type Props = {
  line: OrderLine;
  kind: OrderKind;
  taxes: Tax[];
  mode: 'add' | 'edit';
  onCancel: () => void;
  onSave: (line: OrderLine) => void;
  onRemove?: () => void;
};

function modifierSelection(line: OrderLine): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  line.selectedModifiers.forEach((m) => {
    out[m.mId] = [...(out[m.mId] ?? []), m.id];
  });
  return out;
}

export function LineItemDialog({ line: initial, kind, taxes, mode, onCancel, onSave, onRemove }: Props) {
  const field = kind.field;
  const [line, setLine] = useState<OrderLine>(() => {
    const copy: OrderLine = JSON.parse(JSON.stringify(initial));
    // Show every catalog variation with its quantity (0 = not ordered).
    if (copy.hasVariants && copy.variations.length) {
      copy.selectedVariations = copy.variations.map((v) => {
        const existing = initial.selectedVariations.find((s) => s.id === v.id);
        return existing ?? { id: v.id, meta: v.meta, price: v.price, cost: v.cost, quantity: 0, available: v.available };
      });
      initial.selectedVariations.filter((s) => !copy.variations.some((v) => v.id === s.id)).forEach((s) => copy.selectedVariations.push(s));
    }
    if (copy.portions.length && !copy.selectedPortions.length) {
      copy.selectedPortions = [newSelectedPortion(copy.portions[0], copy.quantity || 1)];
    }
    return copy;
  });
  const [modSel, setModSel] = useState<Record<string, string[]>>(() => modifierSelection(initial));
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof OrderLine>(key: K, value: OrderLine[K]) => setLine((current) => ({ ...current, [key]: value }));

  const usesVariations = line.hasVariants && line.selectedVariations.length > 0;
  const usesPortions = line.portions.length > 0;
  const derivedQuantity = usesVariations
    ? line.selectedVariations.reduce((a, v) => a + num(v.quantity), 0)
    : usesPortions
      ? line.selectedPortions.reduce((a, p) => a + num(p.quantity), 0)
      : num(line.quantity);

  const serialCount = field === 'cost' ? countSerialRanges(line.serialRanges) : line.serials.length;
  const serialsRequired = field === 'price' ? line.serialOptions.length > 0 && line.has_serials : line.has_serials;
  const priceEditable = field === 'cost' || line.changeable;
  const taxesEditable = field === 'cost' || line.changeable;
  const taxOptions = useMemo(() => taxes.map((tax) => ({ value: tax.id, label: `${tax.name} (${num(tax.rate)}%)` })), [taxes]);

  const updateVariation = (id: string, quantity: number) =>
    setLine((current) => ({
      ...current,
      selectedVariations: current.selectedVariations.map((v) => (v.id === id ? { ...v, quantity } : v))
    }));

  const updatePortion = (index: number, patch: Partial<SelectedPortion>) =>
    setLine((current) => ({
      ...current,
      selectedPortions: current.selectedPortions.map((p, i) => (i === index ? { ...p, ...patch } : p))
    }));

  const changePortion = (index: number, portionId: string) => {
    const portion = line.portions.find((p) => p.id === portionId);
    if (!portion) return;
    updatePortion(index, { ...newSelectedPortion(portion, line.selectedPortions[index]?.quantity ?? 1) });
  };

  const changeUnit = (unitId: string) => setLine((current) => ({ ...current, unit_id: unitId, [field]: valueForUnit(current, unitId) }));

  const submit = () => {
    setError(null);
    const quantity = derivedQuantity;
    if (!(quantity > 0)) return setError(t('orders.error.quantity'));
    if (usesVariations && field === 'price') {
      const over = line.selectedVariations.find(
        (v) => num(v.quantity) > 0 && num(v.quantity) > num(v.available) && num(v.available) >= 0 && mode === 'add'
      );
      if (over) return setError(t('orders.error.onlyAvailable', { x: num(over.available) }));
    }
    if (priceEditable && field === 'price') {
      const price = num(line.price);
      if (
        (line.min_price != null && line.min_price > 0 && price < line.min_price) ||
        (line.max_price != null && line.max_price > 0 && price > line.max_price)
      ) {
        return setError(t('orders.error.priceRange', { min: num(line.min_price), max: num(line.max_price) }));
      }
    }
    if (line.discount != null && line.max_discount != null && num(line.discount) > line.max_discount && field === 'price') {
      return setError(t('orders.error.itemMaxDiscount', { percent: line.max_discount }));
    }
    if (serialsRequired && serialCount !== quantity) {
      return setError(t('orders.error.serialCount', { n: serialCount, total: quantity }));
    }
    if (usesPortions) {
      const missing = line.selectedPortions.some((p) => p.choosables.some((c) => !c.selected));
      if (missing) return setError(t('orders.error.choosable'));
    }
    const selectedModifiers: SelectedModifier[] = [];
    line.modifiers.forEach((m) => {
      (modSel[m.id] ?? []).forEach((optionId) => {
        const option = m.options.find((o) => o.id === optionId);
        if (!option) return;
        const previous = line.selectedModifiers.find((s) => s.id === option.id && s.mId === m.id);
        selectedModifiers.push({
          id: option.id,
          mId: m.id,
          title: m.title,
          option: option.name,
          price: option.price,
          cost: option.cost,
          quantity: previous?.quantity ?? 1
        });
      });
    });
    const selectedVariations: SelectedVariation[] = usesVariations ? line.selectedVariations.filter((v) => num(v.quantity) > 0) : [];
    onSave({
      ...line,
      quantity,
      discount: line.discount === null || (line.discount as unknown) === '' ? null : num(line.discount),
      selectedVariations,
      selectedModifiers,
      serialRanges: line.serialRanges,
      catalogLoaded: true
    });
  };

  return (
    <Dialog open onClose={onCancel} maxWidth="md" fullWidth PaperProps={{ sx: { bgcolor: UI.white, backgroundImage: 'none' } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {mode === 'add' ? t('orders.line.selectOptions', { name: line.name }) : t('orders.line.edit', { name: line.name })}
        <Typography variant="body2" color="text.secondary">
          {line.code}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          {!usesVariations && !usesPortions ? (
            <TextField
              label={t('orders.line.quantity')}
              type="number"
              value={line.quantity}
              onChange={(e) => set('quantity', num(e.target.value))}
              inputProps={{ min: 0, step: 'any' }}
              sx={{ maxWidth: 220 }}
            />
          ) : null}

          {usesVariations ? (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {t('orders.line.variations')}
              </Typography>
              <Stack spacing={1}>
                {line.selectedVariations.map((v) => (
                  <Stack key={v.id} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                    <Typography sx={{ flex: 1 }} variant="body2">
                      {metaLabel(v.meta)}
                    </Typography>
                    {field === 'price' ? (
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120 }}>
                        {t('orders.line.available', { x: num(v.available) })}
                      </Typography>
                    ) : null}
                    <TextField
                      size="small"
                      type="number"
                      label={field === 'price' ? t('orders.line.price') : t('orders.line.cost')}
                      value={v[field]}
                      disabled={!priceEditable}
                      onChange={(e) =>
                        setLine((current) => ({
                          ...current,
                          selectedVariations: current.selectedVariations.map((s) =>
                            s.id === v.id ? { ...s, [field]: num(e.target.value) } : s
                          )
                        }))
                      }
                      sx={{ width: 140 }}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label={t('orders.line.quantity')}
                      value={v.quantity}
                      onChange={(e) => updateVariation(v.id, num(e.target.value))}
                      inputProps={{ min: 0, step: 'any' }}
                      sx={{ width: 120 }}
                    />
                  </Stack>
                ))}
              </Stack>
            </Box>
          ) : null}

          {usesPortions ? (
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {t('orders.line.portions')}
                </Typography>
                <Button
                  size="small"
                  startIcon={<Add size={16} />}
                  onClick={() =>
                    setLine((c) => ({ ...c, selectedPortions: [...c.selectedPortions, newSelectedPortion(c.portions[0], 1)] }))
                  }
                >
                  {t('orders.line.addPortion')}
                </Button>
              </Stack>
              <Stack spacing={1.5} divider={<Divider flexItem />}>
                {line.selectedPortions.map((sp, index) => {
                  const portion = line.portions.find((p) => p.id === sp.id) ?? line.portions[0];
                  return (
                    <Stack key={`${sp.id}-${index}`} spacing={1.25}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                        <TextField
                          select
                          size="small"
                          label={t('orders.line.portion')}
                          value={sp.id}
                          onChange={(e) => changePortion(index, e.target.value)}
                          sx={{ minWidth: 200 }}
                        >
                          {line.portions.map((p) => (
                            <MenuItem key={p.id} value={p.id}>
                              {p.name === 'regular' ? t('orders.line.regular') : p.name} ({num(p[field])})
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          size="small"
                          type="number"
                          label={t('orders.line.quantity')}
                          value={sp.quantity}
                          onChange={(e) => updatePortion(index, { quantity: num(e.target.value) })}
                          sx={{ width: 120 }}
                        />
                        {line.selectedPortions.length > 1 ? (
                          <IconButton
                            aria-label={t('orders.line.remove')}
                            onClick={() => setLine((c) => ({ ...c, selectedPortions: c.selectedPortions.filter((_, i) => i !== index) }))}
                          >
                            <Trash size={18} />
                          </IconButton>
                        ) : null}
                      </Stack>
                      {portion?.portion_items.length ? (
                        <Typography variant="caption" color="text.secondary">
                          {t('orders.line.portionItems')}: {portion.portion_items.map((e) => e.name).join(', ')}
                        </Typography>
                      ) : null}
                      {portion?.essentials.length ? (
                        <Typography variant="caption" color="text.secondary">
                          {t('orders.line.essentials')}: {portion.essentials.map((e) => e.name).join(', ')}
                        </Typography>
                      ) : null}
                      {portion?.choosables.map((group, gi) => {
                        const chosen = sp.choosables[gi];
                        const chosenItem = group.items.find((i) => i.item_id === chosen?.selected);
                        return (
                          <Stack key={group.id} direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                            <TextField
                              select
                              size="small"
                              label={group.name || t('orders.line.choosable')}
                              value={chosen?.selected ?? ''}
                              required
                              onChange={(e) => {
                                const item = group.items.find((i) => i.item_id === e.target.value);
                                const next = [...sp.choosables];
                                next[gi] = { id: group.id, selected: e.target.value, variation_id: item?.variations[0]?.id ?? null };
                                updatePortion(index, { choosables: next });
                              }}
                              sx={{ minWidth: 220 }}
                            >
                              {group.items.map((option) => (
                                <MenuItem key={option.item_id} value={option.item_id}>
                                  {option.name}
                                </MenuItem>
                              ))}
                            </TextField>
                            {chosenItem?.variations.length ? (
                              <TextField
                                select
                                size="small"
                                label={t('orders.line.variation')}
                                value={chosen?.variation_id ?? ''}
                                onChange={(e) => {
                                  const next = [...sp.choosables];
                                  next[gi] = { ...next[gi], variation_id: e.target.value };
                                  updatePortion(index, { choosables: next });
                                }}
                                sx={{ minWidth: 220 }}
                              >
                                {chosenItem.variations.map((v) => (
                                  <MenuItem key={v.id} value={v.id}>
                                    {metaLabel(v.meta)}
                                  </MenuItem>
                                ))}
                              </TextField>
                            ) : null}
                          </Stack>
                        );
                      })}
                      {(['essentials', 'portion_items'] as const).map((group) =>
                        (portion?.[group] ?? [])
                          .filter((component) => component.variations.length)
                          .map((component) => {
                            const ci = sp[group].findIndex((c) => c.id === component.id);
                            return (
                              <TextField
                                key={`${group}-${component.id}`}
                                select
                                size="small"
                                label={`${component.name} (${t('orders.line.variation')})`}
                                value={ci >= 0 ? (sp[group][ci].variation_id ?? '') : ''}
                                onChange={(e) => {
                                  const next = [...sp[group]];
                                  if (ci >= 0) next[ci] = { ...next[ci], variation_id: e.target.value };
                                  else next.push({ id: component.id, item_id: component.item_id, variation_id: e.target.value });
                                  updatePortion(index, { [group]: next } as Partial<SelectedPortion>);
                                }}
                                sx={{ maxWidth: 360 }}
                              >
                                {component.variations.map((v) => (
                                  <MenuItem key={v.id} value={v.id}>
                                    {metaLabel(v.meta)}
                                  </MenuItem>
                                ))}
                              </TextField>
                            );
                          })
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          ) : null}

          {line.modifiers.length ? (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {t('orders.line.modifiers')}
              </Typography>
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                {line.modifiers.map((m) => {
                  const selected = modSel[m.id] ?? [];
                  const label = (o: { name: string; price: number; cost: number }) => `${o.name} (+${num(o[field])})`;
                  const multiple = m.show_as === 'checkbox' || m.show_as === 'select_multiple';
                  return (
                    <Box key={m.id}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {m.title}
                      </Typography>
                      {m.show_as === 'radio' ? (
                        <RadioGroup
                          value={selected[0] ?? ''}
                          onChange={(e) => setModSel((c) => ({ ...c, [m.id]: e.target.value ? [e.target.value] : [] }))}
                        >
                          {m.options.map((o) => (
                            <FormControlLabel key={o.id} value={o.id} control={<Radio size="small" />} label={label(o)} />
                          ))}
                        </RadioGroup>
                      ) : m.show_as === 'checkbox' ? (
                        <FormGroup>
                          {m.options.map((o) => (
                            <FormControlLabel
                              key={o.id}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={selected.includes(o.id)}
                                  onChange={(e) =>
                                    setModSel((c) => ({
                                      ...c,
                                      [m.id]: e.target.checked ? [...selected, o.id] : selected.filter((s) => s !== o.id)
                                    }))
                                  }
                                />
                              }
                              label={label(o)}
                            />
                          ))}
                        </FormGroup>
                      ) : (
                        <TextField
                          select
                          fullWidth
                          size="small"
                          SelectProps={{ multiple }}
                          value={multiple ? selected : (selected[0] ?? '')}
                          onChange={(e) => {
                            const value = e.target.value as unknown;
                            setModSel((c) => ({ ...c, [m.id]: Array.isArray(value) ? (value as string[]) : value ? [String(value)] : [] }));
                          }}
                          sx={{ mt: 0.5 }}
                        >
                          {!multiple ? <MenuItem value="">{t('common.none')}</MenuItem> : null}
                          {m.options.map((o) => (
                            <MenuItem key={o.id} value={o.id}>
                              {label(o)}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ) : null}

          {field === 'price' && line.serialOptions.length ? (
            <Autocomplete
              multiple
              noOptionsText={t('orders.noOptions')}
              options={line.serialOptions}
              getOptionLabel={(o) => o.number}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={line.serialOptions.filter((o) => line.serials.includes(o.id))}
              onChange={(_e, value) =>
                set(
                  'serials',
                  value.map((v) => v.id)
                )
              }
              renderTags={(value, getTagProps) =>
                value.map((o, i) => <Chip size="small" label={o.number} {...getTagProps({ index: i })} key={o.id} />)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('orders.line.serials')}
                  helperText={t('orders.line.serialCount', { n: serialCount, total: derivedQuantity })}
                />
              )}
            />
          ) : null}

          {field === 'cost' && line.has_serials ? (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {t('orders.line.serials')}
              </Typography>
              <Stack spacing={1}>
                {line.serialRanges.map((range, index) => (
                  <Stack key={index} direction="row" spacing={1.5} alignItems="center">
                    <TextField
                      size="small"
                      label={t('orders.line.serialNumber')}
                      value={range.number}
                      onChange={(e) =>
                        set(
                          'serialRanges',
                          line.serialRanges.map((r, i) => (i === index ? { ...r, number: e.target.value } : r))
                        )
                      }
                    />
                    <TextField
                      size="small"
                      label={t('orders.line.till')}
                      value={range.till}
                      onChange={(e) =>
                        set(
                          'serialRanges',
                          line.serialRanges.map((r, i) => (i === index ? { ...r, till: e.target.value } : r))
                        )
                      }
                    />
                    <IconButton
                      aria-label={t('orders.line.remove')}
                      onClick={() =>
                        set(
                          'serialRanges',
                          line.serialRanges.filter((_, i) => i !== index)
                        )
                      }
                    >
                      <Trash size={18} />
                    </IconButton>
                  </Stack>
                ))}
                <Box>
                  <Button
                    size="small"
                    startIcon={<Add size={16} />}
                    onClick={() => set('serialRanges', [...line.serialRanges, { number: '', till: '' }])}
                  >
                    {t('orders.line.addSerial')}
                  </Button>
                </Box>
                <Typography variant="caption" color={serialCount > derivedQuantity ? 'error' : 'text.secondary'}>
                  {t('orders.line.serialCount', { n: serialCount, total: derivedQuantity })}
                </Typography>
              </Stack>
            </Box>
          ) : null}

          <Divider textAlign="left">
            <Typography variant="caption" color="text.secondary">
              {t('orders.line.general')}
            </Typography>
          </Divider>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            {line.units.length > 1 ? (
              <TextField select label={t('orders.line.unit')} value={line.unit_id ?? ''} onChange={(e) => changeUnit(e.target.value)}>
                {line.units.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
            {!usesVariations && !usesPortions ? (
              <TextField
                label={field === 'price' ? t('orders.line.price') : t('orders.line.cost')}
                type="number"
                value={line[field]}
                disabled={!priceEditable}
                helperText={
                  !priceEditable
                    ? t('orders.line.priceLocked')
                    : line.min_price || line.max_price
                      ? t('orders.line.priceRange', { min: num(line.min_price), max: num(line.max_price) })
                      : undefined
                }
                onChange={(e) => set(field, num(e.target.value))}
                inputProps={{ step: 'any' }}
              />
            ) : null}
            <TextField
              label={t('orders.line.discount')}
              type="number"
              value={line.discount ?? ''}
              onChange={(e) => set('discount', e.target.value === '' ? null : num(e.target.value))}
              helperText={
                line.max_discount != null && field === 'price' ? t('orders.line.maxDiscount', { percent: line.max_discount }) : undefined
              }
              inputProps={{ min: 0, step: 'any' }}
            />
            {field === 'price' && line.allPromotions.length ? (
              <Autocomplete
                multiple
                noOptionsText={t('orders.noOptions')}
                options={line.allPromotions}
                getOptionLabel={(p) => p.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={line.allPromotions.filter((p) => line.promotions.includes(p.id))}
                onChange={(_e, value) =>
                  set(
                    'promotions',
                    value.map((p) => p.id)
                  )
                }
                renderInput={(params) => <TextField {...params} label={t('orders.line.promotions')} />}
              />
            ) : null}
            {field === 'cost' ? (
              <TextField label={t('orders.line.batchNo')} value={line.batch_no} onChange={(e) => set('batch_no', e.target.value)} />
            ) : null}
            {field === 'cost' && line.expiry ? (
              <TextField
                label={t('orders.line.expiryDate')}
                type="date"
                value={line.expiry_date}
                onChange={(e) => set('expiry_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: new Date().toISOString().slice(0, 10) }}
              />
            ) : null}
          </Box>
          {taxesEditable ? (
            <Autocomplete
              multiple
              noOptionsText={t('orders.noOptions')}
              options={taxOptions}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              value={taxOptions.filter((o) => line.allTaxes.includes(o.value))}
              onChange={(_e, value) =>
                set(
                  'allTaxes',
                  value.map((v) => v.value)
                )
              }
              renderInput={(params) => <TextField {...params} label={t('orders.line.taxes')} />}
            />
          ) : line.allTaxes.length ? (
            <Typography variant="body2" color="text.secondary">
              {t('orders.line.taxes')}:{' '}
              {taxes
                .filter((tax) => line.allTaxes.includes(tax.id))
                .map((tax) => tax.name)
                .join(', ') || line.allTaxes.length}
            </Typography>
          ) : null}
          <TextField
            label={t('orders.line.comment')}
            placeholder={t('orders.line.commentPlaceholder')}
            multiline
            minRows={2}
            value={line.comment}
            onChange={(e) => set('comment', e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        <Box>
          {onRemove ? (
            <Button color="error" startIcon={<Trash size={16} />} onClick={onRemove}>
              {t('orders.line.removeFromOrder')}
            </Button>
          ) : null}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button onClick={onCancel}>{t('action.cancel')}</Button>
          <Button variant="contained" onClick={submit}>
            {mode === 'add' ? t('orders.line.addToOrder') : t('orders.line.update')}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
